import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Save, Check, Search, CircleMinus, CircleCheck } from 'lucide-react';
import Skeleton from '../../common/Skeleton';
import FormControl from '../../common/FormControl';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Pagination from '../../common/Pagination';
import Toast, { ToastMessage } from '../../common/Toast';
import { confirmWarning, confirmActive } from '../../common/confirm';
import NoDataFound from '../../common/NoDataFound';
import { ProcedureTemplate, QuestionnaireTemplateItem, questionnaireTemplateService } from '../../../services/auditService';
import { extractApiError } from '../../../utils/apiError';

interface ProcedureQuestionnaireProps {
    procedure: ProcedureTemplate;
}

interface FormState {
    question_text: string;
    evidence_type: string;
}

const EVIDENCE_TYPES = ['None', 'Document', 'Screenshot', 'Excel', 'Text Explanation', 'External Link'];
const EVIDENCE_OPTIONS = EVIDENCE_TYPES.map(t => ({ value: t, label: t }));

const blank = (): FormState => ({ question_text: '', evidence_type: 'None' });

const ProcedureQuestionnaire: React.FC<ProcedureQuestionnaireProps> = ({ procedure }) => {
    const [items, setItems] = useState<QuestionnaireTemplateItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [toggling, setToggling] = useState(false);
    const [isModalOpen, setModalOpen] = useState(false);
    const [currentQ, setCurrentQ] = useState<QuestionnaireTemplateItem | null>(null);
    const [formData, setFormData] = useState<FormState>(blank);
    const [toast, setToast] = useState<ToastMessage | null>(null);

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const list = await questionnaireTemplateService.listForProcedure(procedure.id);
            setItems(list);
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load questions.') });
        } finally {
            setLoading(false);
        }
    }, [procedure.id]);

    useEffect(() => { reload(); }, [reload]);

    useEffect(() => { setPage(1); }, [searchTerm, pageSize]);

    useEffect(() => {
        if (isModalOpen) {
            setSubmitting(false);
            setToggling(false);
        }
    }, [isModalOpen, currentQ]);

    const filtered = items.filter(q => q.question_text.toLowerCase().includes(searchTerm.toLowerCase()));
    const totalRecords = filtered.length;
    const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

    const handleAdd = useCallback(() => {
        setCurrentQ(null);
        setFormData(blank());
        setModalOpen(true);
    }, []);

    const handleEdit = (q: QuestionnaireTemplateItem) => {
        setCurrentQ(q);
        const evType = q.evidence_type ? q.evidence_type : (q.evidence_required ? 'Document' : 'None');
        setFormData({ question_text: q.question_text, evidence_type: evType });
        setModalOpen(true);
    };

    const handleToggleActive = async () => {
        if (!currentQ) return;
        const isActive = (currentQ.is_active ?? 1) === 1;
        const next = isActive ? 0 : 1;
        const confirmFn = isActive ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this question?' : 'Inactivate this question?',
            text: next
                ? 'It will appear in active lists again.'
                : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        setToggling(true);
        try {
            await questionnaireTemplateService.update(currentQ.id, { is_active: next });
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated' : 'Inactivated' });
            setModalOpen(false);
            await reload();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to update status.') });
        } finally {
            setToggling(false);
        }
    };

    const handleSave = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!formData.question_text.trim()) return;
        const required = formData.evidence_type !== 'None';
        const payload = {
            question_text: formData.question_text,
            evidence_required: required,
            evidence_type: required ? formData.evidence_type : null,
        };
        setSubmitting(true);
        try {
            if (currentQ) {
                await questionnaireTemplateService.update(currentQ.id, payload);
                setToast({ type: 'success', message: 'Question updated.' });
            } else {
                await questionnaireTemplateService.create({ procedure_template_id: procedure.id, is_active: 1, ...payload });
                setToast({ type: 'success', message: 'Question created.' });
            }
            setModalOpen(false);
            await reload();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Save failed.') });
        } finally {
            setSubmitting(false);
        }
    };

    const isEditMode = !!currentQ;
    const isQuestionActive = (currentQ?.is_active ?? 1) === 1;

    return (
        <>
            <div className="netbody-header">
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_questionnaire_template_search"
                    />
                    <Button variant="fill" icon={Plus} data-testid="BTN_questionnaire_template_create" onClick={handleAdd}>Create</Button>
                </div>
            </div>
            <div className="table-list flex-1 min-h-0">
                <div className="table-list-scroll">
                    <table className="table-list-table">
                        <thead>
                            <tr>
                                <th className="table-list-th table-list-cell--sno">S.No</th>
                                <th className="table-list-th">Question</th>
                                <th className="table-list-th">Evidence</th>
                            </tr>
                        </thead>
                        <tbody aria-busy={loading} aria-live="polite">
                            {loading && <Skeleton.TableRows cols={['3xs', 'md', 'pill']} />}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan={3} className="table-list-cell"><NoDataFound title="No Questions" message="No questions defined yet." /></td></tr>
                            )}
                            {!loading && paginatedRows.map((q, idx) => (
                                <tr key={q.id} className="table-list-row" data-testid={`BTN_questionnaire_template_row_${q.id}_open`} onClick={() => handleEdit(q)}>
                                    <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                                    <td className="table-list-cell">
                                        <span className="table-list-cell-title">{q.question_text}</span>
                                    </td>
                                    <td className="table-list-cell">
                                        {q.evidence_type && q.evidence_type !== 'None' ? (
                                            <span className="status-badge xs info">{q.evidence_type}</span>
                                        ) : q.evidence_required ? (
                                            <span className="status-badge xs danger">Required</span>
                                        ) : (
                                            <span className="text-gray-400 text-xs italic">No evidence</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalRecords={totalRecords}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    recordLabel="questions"
                />
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setModalOpen(false)} size="md">
                <Modal.Header>
                    {isEditMode
                        ? <>Editing "<span className="item-highlight">{currentQ!.question_text}</span>"</>
                        : 'Create Question'}
                </Modal.Header>
                <Modal.Body>
                    <form id="questionnaire-item-form" data-testid="MOD_questionnaire_template_form" onSubmit={handleSave} className="flex flex-col gap-y-4">
                        <FormControl.Textarea
                            label="Question Text"
                            required
                            data-testid="INP_questionnaire_template_text"
                            placeholder="Enter your question here…"
                            value={formData.question_text}
                            onChange={e => setFormData({ ...formData, question_text: e.target.value })}
                            autoFocus
                        />
                        <FormControl.SearchSelect
                            label="Expected Evidence"
                            data-testid="SEL_questionnaire_template_evidence"
                            options={EVIDENCE_OPTIONS}
                            value={formData.evidence_type}
                            onChange={v => setFormData({ ...formData, evidence_type: String(v ?? 'None') })}
                        />
                    </form>
                </Modal.Body>
                <Modal.Footer className={isEditMode ? 'app-modal-footer--split' : ''}>
                    {isEditMode && (
                        <Button
                            variant={isQuestionActive ? 'inactive-outline' : 'active-outline'}
                            icon={isQuestionActive ? CircleMinus : CircleCheck}
                            data-testid="BTN_questionnaire_template_toggle_active"
                            onClick={handleToggleActive}
                            disabled={toggling || submitting}
                        >
                            {toggling ? '...' : isQuestionActive ? 'Inactive' : 'Active'}
                        </Button>
                    )}
                    <Button
                        type="submit"
                        form="questionnaire-item-form"
                        variant={isEditMode ? 'update' : 'fill'}
                        icon={isEditMode ? Check : Save}
                        data-testid="BTN_questionnaire_template_submit"
                        disabled={submitting || toggling || !formData.question_text.trim()}
                    >
                        {submitting
                            ? isEditMode ? 'Updating...' : 'Submitting...'
                            : isEditMode ? 'Update' : 'Submit'}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
};

export default ProcedureQuestionnaire;
